import React, { useState } from 'react';
import { Box, Text, Paper, ActionIcon, Tooltip, Modal, useMantineColorScheme, Group, Flex } from '@mantine/core';
import { IconCopy, IconArrowsMaximize, IconArrowsMinimize, IconMaximize } from '@tabler/icons-react';
import AceEditor from 'react-ace';
import 'ace-builds';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-github';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { NON_INDEXED_PROPERTY_STUB } from '../../utils/entityPropertyUtils';

export type SqlExpandedState = 'collapsed' | 'normal' | 'expanded';

export interface SqlDisplayComponentProps {
    /** Raw value: string, null, undefined, or unindexed stub. Copy/expand only shown when real text. */
    value: string | null | undefined;
    /** Optional label for copy tooltip (e.g. attr.displayName). */
    displayName?: string;
    /** Optional id for Ace editor instance (e.g. field key). */
    editorId?: string;
}

function hasSqlContent(value: string | null | undefined): boolean {
    if (value == null || value === NON_INDEXED_PROPERTY_STUB) return false;
    if (typeof value !== 'string') return false;
    return value.trim() !== '';
}

/**
 * Encapsulates display of SqlStatement / SqlStatementExplicit: empty state, Ace SQL viewer,
 * copy (only when there is real text and not unindexed stub), expand/collapse, and full-screen (no URL).
 */
export const SqlDisplayComponent: React.FC<SqlDisplayComponentProps> = ({
    value,
    displayName = 'SQL',
    editorId = 'sql'
}) => {
    const { colorScheme } = useMantineColorScheme();
    const [sqlCopied, setSqlCopied] = useState(false);
    const [sqlExpanded, setSqlExpanded] = useState<SqlExpandedState>('normal');
    const [sqlFullScreenOpen, setSqlFullScreenOpen] = useState(false);

    const hasContent = hasSqlContent(value);
    const sqlString = typeof value === 'string' ? value : '';

    const handleCopy = async () => {
        const success = await copyToClipboard(sqlString);
        if (success) {
            setSqlCopied(true);
            setTimeout(() => setSqlCopied(false), 2000);
        }
    };

    const cycleExpand = () => {
        if (sqlExpanded === 'collapsed') setSqlExpanded('normal');
        else if (sqlExpanded === 'normal') setSqlExpanded('expanded');
        else setSqlExpanded('collapsed');
    };

    if (!hasContent) {
        return (
            <Text size="sm" c="dimmed">
                No SQL statement specified.
            </Text>
        );
    }

    const aceSqlProps = {
        mode: 'sql' as const,
        theme: colorScheme === 'dark' ? 'monokai' : 'github',
        value: sqlString,
        readOnly: true as const,
        editorProps: { $blockScrolling: true },
        width: '100%',
        fontSize: 14,
        showPrintMargin: false,
        showGutter: true,
        highlightActiveLine: false,
        setOptions: {
            showLineNumbers: true,
            tabSize: 2,
            useWorker: false,
            foldStyle: 'markbeginend' as const,
            showFoldWidgets: true,
        },
    };

    return (
        <>
            <Paper style={{ border: '2px solid var(--mantine-color-default-border)', marginTop: 4 }}>
                <Group gap={4} align="center" px="xs" py={4} style={{ borderBottom: '2px solid var(--mantine-color-default-border)' }}>
                    <Tooltip label={sqlCopied ? 'Copied!' : `Copy ${displayName} to clipboard`}>
                        <ActionIcon variant="subtle" size="sm" onClick={handleCopy}>
                            <IconCopy size={16} color={sqlCopied ? 'var(--mantine-color-green-6)' : undefined} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label={
                        sqlExpanded === 'expanded' ? 'Collapse to normal' :
                            sqlExpanded === 'normal' ? 'Minimize to single line' : 'Expand to full size'
                    }>
                        <ActionIcon variant="subtle" size="sm" onClick={cycleExpand}>
                            {sqlExpanded !== 'collapsed' ? <IconArrowsMinimize size={16} /> : <IconArrowsMaximize size={16} />}
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Full screen" withArrow>
                        <ActionIcon
                            variant="subtle"
                            size="sm"
                            aria-label="Open SQL in full screen"
                            onClick={() => setSqlFullScreenOpen(true)}
                        >
                            <IconMaximize size={16} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
                {sqlExpanded === 'collapsed' ? (
                    <Flex
                        align="center"
                        h={24}
                        px="xs"
                        py={2}
                        ff="monospace"
                        fz={12}
                        c="dimmed"
                        style={{
                            overflow: 'hidden',
                            backgroundColor: 'var(--mantine-color-default-hover)',
                        }}
                    >
                        <Text size="xs" ff="monospace" w="100%" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sqlString.substring(0, 100)}...
                        </Text>
                    </Flex>
                ) : (
                    <AceEditor
                        {...aceSqlProps}
                        name={`sql-viewer-${editorId}`}
                        height={sqlExpanded === 'expanded' ? '600px' : '400px'}
                    />
                )}
            </Paper>
            <Modal
                opened={sqlFullScreenOpen}
                onClose={() => setSqlFullScreenOpen(false)}
                size="100vw"
                title={displayName}
                styles={{
                    content: { height: '95vh', maxHeight: '95vh', display: 'flex', flexDirection: 'column' },
                    body: {
                        flex: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 'var(--mantine-spacing-md)',
                    },
                    header: { padding: 'var(--mantine-spacing-md)' },
                }}
            >
                <Box flex={1} style={{ minHeight: 0 }}>
                    <AceEditor
                        {...aceSqlProps}
                        name={`sql-viewer-fullscreen-${editorId}`}
                        height="calc(95vh - 140px)"
                    />
                </Box>
            </Modal>
        </>
    );
};
